import type { KpiDay, MonetizationFreshness } from '@/types';
import { DASH, dayLabel, fmtPct } from '../bq-insights/bqMath';
import { formatInr } from '../today/todayMath';
import type { Target } from './targets';

export type TargetStatus = 'met' | 'on-track' | 'behind' | 'no-data';

const known = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * How far `actual` is from the baseline to the target, 0..1 (clamped). Null without an actual or
 * a baseline. A baseline equal to the target has no distance to cover: 1 once reached, else 0.
 */
export function progress(actual: number | null, t: Target): number | null {
  if (!known(actual) || t.baseline === null) return null;
  const span = t.target - t.baseline;
  if (span === 0) return met(actual, t) ? 1 : 0;
  return Math.min(1, Math.max(0, (actual - t.baseline) / span));
}

function met(actual: number, t: Target): boolean {
  return t.higherIsBetter ? actual >= t.target : actual <= t.target;
}

/**
 * met: the target is reached; on-track: at least half of the way from the baseline; behind:
 * otherwise (with no baseline, never on-track); no-data: no actual.
 */
export function targetStatus(actual: number | null, t: Target): TargetStatus {
  if (!known(actual)) return 'no-data';
  if (met(actual, t)) return 'met';
  const p = progress(actual, t);
  return p !== null && p >= 0.5 ? 'on-track' : 'behind';
}

/** n / d; null when either is unknown or d is 0. */
export function rate(n: number | null | undefined, d: number | null | undefined): number | null {
  if (!known(n) || !known(d) || d === 0) return null;
  return n / d;
}

/** Σ of the known values; null when none is known (so an unknown column stays unknown). */
export function sumOf<T>(rows: T[], pick: (row: T) => number | null | undefined): number | null {
  let total: number | null = null;
  for (const row of rows) {
    const v = pick(row);
    if (known(v)) total = (total ?? 0) + v;
  }
  return total;
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/** The longest range the backend answers, in days. */
export const MAX_RANGE_DAYS = 92;

/** 'YYYY-MM-DD' of the IST day containing the instant `ms`. */
export function istDay(ms: number): string {
  return new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/** 'YYYY-MM-DD' shifted by whole days. */
export function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

/** Days from `from` (included) to `to` (excluded). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

/** The last 7 IST days ending yesterday: `to` is IST today, excluded. */
export function defaultRange(nowMs: number): { from: string; to: string } {
  const to = istDay(nowMs);
  return { from: addDays(to, -7), to };
}

/** The query window for a picked inclusive range: `to` is the day after the last picked day. */
export function windowForPicked(first: string, last: string): { from: string; to: string } {
  return { from: first, to: addDays(last, 1) };
}

/** True when a picked inclusive range is longer than the backend allows. */
export function pickedTooLong(first: string, last: string): boolean {
  return daysBetween(first, addDays(last, 1)) > MAX_RANGE_DAYS;
}

/** 0.0033 -> '0.33%' (fraction in, `decimals` after the point); null -> '—'. */
export function fmtPctN(v: number | null | undefined, decimals = 2): string {
  return known(v) ? `${(v * 100).toFixed(decimals)}%` : DASH;
}

/** 0.0123 -> '$0.0123'; null -> '—'. */
export const usd4 = (v: number | null | undefined): string =>
  known(v) ? `$${v.toFixed(4)}` : DASH;

/** 7500 -> '₹7,500'; null -> '—'. */
export const inr = (v: number | null | undefined): string => (known(v) ? formatInr(v) : DASH);

/** A difference in fractions as percentage points: 0.0035 -> '+0.35 pt'; null -> '—'. */
export function fmtPoints(v: number | null | undefined, decimals = 2): string {
  if (!known(v)) return DASH;
  const pts = v * 100;
  return `${pts > 0 ? '+' : ''}${pts.toFixed(decimals)} pt`;
}

/** A confidence interval '[low, high]' in points; '—' unless both ends are known. */
export function fmtInterval(
  low: number | null | undefined,
  high: number | null | undefined,
  decimals = 2
): string {
  if (!known(low) || !known(high)) return DASH;
  const pts = (v: number) => `${v > 0 ? '+' : ''}${(v * 100).toFixed(decimals)}`;
  return `[${pts(low)}, ${pts(high)}] pt`;
}

/** 'excludes-positive' / 'excludes-negative' when the interval leaves out 0; null otherwise. */
export function ciSide(
  low: number | null | undefined,
  high: number | null | undefined
): 'positive' | 'negative' | null {
  if (!known(low) || !known(high)) return null;
  if (low > 0) return 'positive';
  if (high < 0) return 'negative';
  return null;
}

/**
 * A freshness date for the banner line: null -> 'not yet'; 'YYYY-MM-DD' -> '18 Oct'; a timestamp
 * -> '18 Oct 14:30 IST'; anything else as it came.
 */
export function freshnessLabel(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return 'not yet';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return dayLabel(v);
  const ms = Date.parse(v);
  if (Number.isNaN(ms)) return v;
  const ist = new Date(ms + IST_OFFSET_MS);
  const hh = String(ist.getUTCHours()).padStart(2, '0');
  const mm = String(ist.getUTCMinutes()).padStart(2, '0');
  return `${dayLabel(istDay(ms))} ${hh}:${mm} IST`;
}

/**
 * The payer count of a day's revenue source: backend purchasers on a backend day, the GA4 count
 * otherwise. It matches the day's source tag and its payer rate; null when that count is unknown.
 */
export function dayPayers(
  d: Pick<KpiDay, 'iapRevenueSource' | 'payers' | 'payersBackend'>
): number | null {
  return d.iapRevenueSource === 'backend' ? d.payersBackend : d.payers;
}

/** 'backend 3/7 days' for a row whose backend columns cover only some of its days. */
export function backendDaysNote(backendDays: number, days: number): string {
  return `backend ${backendDays}/${days} days`;
}

/** 'GA4 built through 18 Oct, purchases exported through not yet, scores as of 18 Oct'. */
export function freshnessLine(f: MonetizationFreshness): string {
  return `GA4 built through ${freshnessLabel(f.gaBuiltThrough)}, purchases exported through ${freshnessLabel(f.purchasesExportedThrough)}, scores as of ${freshnessLabel(f.scoresAsOf)}`;
}

/** Slot 'unknown' is the home popup on 0.4.x builds, which send no slot. */
export function slotLabel(slot: string): string {
  return slot === 'unknown' ? 'Home popup (0.4.x)' : slot;
}

/** The daily payer rate of a segment: payerDays / playerDays; '—' when playerDays is 0 or unknown. */
export function dailyPayerRate(payerDays: number | null, playerDays: number | null): string {
  const r = rate(payerDays, playerDays);
  return r === null ? DASH : fmtPct(r);
}
